import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { History as HistoryIcon, SupportAgent as SupportIcon } from '@mui/icons-material';
import useChangeHistory from '../hooks/useChangeHistory';

const BOOLEAN_FIELDS = new Set(['is_featured', 'is_customizable', 'client_visible']);

const humanizeField = (field) => field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function formatTimestamp(value, language) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (error) {
    return date.toLocaleString();
  }
}

function ChangeLine({ field, change, t }) {
  const label = t(`changeHistory.fields.${field}`, { defaultValue: humanizeField(field) });
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return t('changeHistory.emptyValue');
    if (BOOLEAN_FIELDS.has(field)) return Number(value) ? t('changeHistory.yes') : t('changeHistory.no');
    return String(value);
  };

  let detail;
  if (Array.isArray(change?.added) || Array.isArray(change?.removed)) {
    detail = [
      change.added?.length ? t('changeHistory.added', { items: change.added.join(', ') }) : null,
      change.removed?.length ? t('changeHistory.removed', { items: change.removed.join(', ') }) : null,
    ].filter(Boolean).join(' · ');
  } else if (change?.changed) {
    detail = t('changeHistory.updated');
  } else {
    detail = t('changeHistory.fromTo', { from: formatValue(change?.from), to: formatValue(change?.to) });
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{label}</Box>
      {': '}
      {detail}
    </Typography>
  );
}

function HistoryEntry({ entry, t, language }) {
  const metadata = entry.metadata || {};
  const actorName = entry.actor?.label
    || t(`changeHistory.actors.${entry.actor?.type || 'unknown'}`, { defaultValue: t('changeHistory.actors.unknown') });
  const changes = Object.entries(entry.changes || {});

  return (
    <Box
      data-testid={`change-history-entry-${entry.id}`}
      sx={{
        position: 'relative',
        pl: 3,
        pb: 3,
        borderLeft: 2,
        borderColor: 'divider',
        '&:last-of-type': { pb: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: -7,
          top: 4,
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: entry.actor?.bySupport ? 'warning.main' : 'primary.main',
        },
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t(`changeHistory.actions.${entry.action}`, {
          defaultValue: t('changeHistory.actions.unknown'),
          itemName: metadata.itemName || metadata.title || '',
          count: metadata.count || 0,
        })}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, mb: changes.length ? 1 : 0, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          {t('changeHistory.byActor', { name: actorName, time: formatTimestamp(entry.updatedAt || entry.createdAt, language) })}
        </Typography>
        {entry.actor?.bySupport && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<SupportIcon />}
            label={t('changeHistory.bySupport')}
            data-testid={`change-history-support-${entry.id}`}
          />
        )}
      </Stack>
      {changes.length > 0 && (
        <Stack spacing={0.5}>
          {changes.map(([field, change]) => (
            <ChangeLine key={field} field={field} change={change} t={t} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Timeline of recorded changes for a trip or package.
 */
function ChangeHistoryPanel({ endpoint, requestConfig, subtitle, testId = 'change-history-panel' }) {
  const { t, i18n } = useTranslation();
  const { history, loading, failed, reload } = useChangeHistory(endpoint, { requestConfig });

  return (
    <Box data-testid={testId}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('changeHistory.title')}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {subtitle || t('changeHistory.subtitle')}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} aria-label={t('changeHistory.loading')} />
        </Box>
      )}

      {!loading && failed && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={reload}>{t('changeHistory.retry')}</Button>}
        >
          {t('changeHistory.loadError')}
        </Alert>
      )}

      {!loading && !failed && history.length === 0 && (
        <Typography color="text.secondary" data-testid="change-history-empty">
          {t('changeHistory.empty')}
        </Typography>
      )}

      {!loading && !failed && history.length > 0 && (
        <Box sx={{ pl: 1 }}>
          {history.map((entry) => (
            <HistoryEntry key={entry.id} entry={entry} t={t} language={i18n.language} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default React.memo(ChangeHistoryPanel);
